import React, { useState } from 'react';
import { X, Save, User } from 'lucide-react';
import { HealthProfessional, Specialty } from '../../types';

interface HealthProfessionalFormProps {
  onSubmit: (professional: Omit<HealthProfessional, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onCancel: () => void;
  specialties: Specialty[];
  isLoading?: boolean;
  editingProfessional?: HealthProfessional | null;
  fieldErrors?: Record<string, string>;
}

const HealthProfessionalForm: React.FC<HealthProfessionalFormProps> = ({
  onSubmit,
  onCancel,
  specialties,
  isLoading = false,
  editingProfessional = null,
  fieldErrors = {}
}) => {
  const [formData, setFormData] = useState({
    name: editingProfessional?.name || '',
    email: editingProfessional?.email || '', // ← ADD THIS REQUIRED FIELD
    practiceFocus: editingProfessional?.practiceFocus || '',
    photoUrl: editingProfessional?.photoUrl || '',
    availableToday: editingProfessional?.availableToday ?? true,
    callbackTime: editingProfessional?.callbackTime || 'Within 30 minutes',
    yearsExperience: editingProfessional?.yearsExperience || 1,
    rating: editingProfessional?.rating || 5.0,
    selectedSpecialties: editingProfessional?.specialties || [],
    timezone: editingProfessional?.timezone || 'Asia/Yangon'
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Merge field errors from parent component
  const allErrors = { ...errors, ...fieldErrors };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    // Add email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.practiceFocus.trim()) {
      newErrors.practiceFocus = 'Practice focus is required';
    }

    if (formData.yearsExperience < 0 || formData.yearsExperience > 50) {
      newErrors.yearsExperience = 'Years of experience must be between 0 and 50';
    }

    if (formData.rating < 0 || formData.rating > 5) {
      newErrors.rating = 'Rating must be between 0 and 5';
    }

    if (formData.selectedSpecialties.length === 0) {
      newErrors.specialties = 'At least one specialty is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    onSubmit({
      name: formData.name.trim(),
      email: formData.email.trim(), // ← NOW REQUIRED
      practiceFocus: formData.practiceFocus.trim(),
      photoUrl: formData.photoUrl.trim() || '',
      availableToday: formData.availableToday,
      callbackTime: formData.callbackTime,
      yearsExperience: formData.yearsExperience,
      rating: formData.rating,
      specialties: formData.selectedSpecialties,
      timezone: formData.timezone
    });
  };

  const handleSpecialtyToggle = (specialtyName: string) => {
    setFormData(prev => ({
      ...prev,
      selectedSpecialties: prev.selectedSpecialties.includes(specialtyName)
        ? prev.selectedSpecialties.filter(s => s !== specialtyName)
        : [...prev.selectedSpecialties, specialtyName]
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-luma-text flex items-center">
            <User className="w-5 h-5 mr-2" />
            {editingProfessional ? 'Edit Health Professional' : 'Add Health Professional'}
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-luma-text">Basic Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-luma-primary focus:border-transparent ${
                    allErrors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Dr. Sarah Johnson"
                />
                {allErrors.name && <p className="text-red-500 text-sm mt-1">{allErrors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Doctor Email Address *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-luma-primary focus:border-transparent ${
                    allErrors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="doctor@hospital.com"
                  required
                />
                {allErrors.email && <p className="text-red-500 text-sm mt-1">{allErrors.email}</p>}
                <small className="text-gray-500 text-xs mt-1">
                  A password reset email will be sent to verify this address
                </small>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Practice Focus *
              </label>
              <input
                type="text"
                value={formData.practiceFocus}
                onChange={(e) => setFormData(prev => ({ ...prev, practiceFocus: e.target.value }))}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-luma-primary focus:border-transparent ${
                  allErrors.practiceFocus ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Women's Health Specialist"
              />
              {allErrors.practiceFocus && <p className="text-red-500 text-sm mt-1">{allErrors.practiceFocus}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Photo URL (optional)
              </label>
              <input
                type="url"
                value={formData.photoUrl}
                onChange={(e) => setFormData(prev => ({ ...prev, photoUrl: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-luma-primary focus:border-transparent"
                placeholder="https://example.com/photo.jpg"
              />
            </div>
          </div>

          {/* Professional Details */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-luma-text">Professional Details</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Years of Experience *
                </label>
                <input
                  type="number"
                  min="0"
                  max="50"
                  value={formData.yearsExperience}
                  onChange={(e) => setFormData(prev => ({ ...prev, yearsExperience: parseInt(e.target.value) || 0 }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-luma-primary focus:border-transparent ${
                    allErrors.yearsExperience ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {allErrors.yearsExperience && <p className="text-red-500 text-sm mt-1">{allErrors.yearsExperience}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rating *
                </label>
                <input
                  type="number"
                  min="0"
                  max="5"
                  step="0.1"
                  value={formData.rating}
                  onChange={(e) => setFormData(prev => ({ ...prev, rating: parseFloat(e.target.value) || 0 }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-luma-primary focus:border-transparent ${
                    allErrors.rating ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {allErrors.rating && <p className="text-red-500 text-sm mt-1">{allErrors.rating}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Callback Time
                </label>
                <select
                  value={formData.callbackTime}
                  onChange={(e) => setFormData(prev => ({ ...prev, callbackTime: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-luma-primary focus:border-transparent"
                >
                  <option value="Within 15 minutes">Within 15 minutes</option>
                  <option value="Within 30 minutes">Within 30 minutes</option>
                  <option value="Within 1 hour">Within 1 hour</option>
                  <option value="Within 2 hours">Within 2 hours</option>
                  <option value="Same day">Same day</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Timezone *
                </label>
                <select
                  value={formData.timezone}
                  onChange={(e) => setFormData(prev => ({ ...prev, timezone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-luma-primary focus:border-transparent"
                >
                  <option value="Asia/Yangon">Myanmar (Yangon) - UTC+6:30</option>
                  <option value="Asia/Singapore">Singapore - UTC+8</option>
                  <option value="Asia/Bangkok">Thailand (Bangkok) - UTC+7</option>
                  <option value="Asia/Tokyo">Japan (Tokyo) - UTC+9</option>
                  <option value="Asia/Hong_Kong">Hong Kong - UTC+8</option>
                  <option value="Asia/Manila">Philippines (Manila) - UTC+8</option>
                  <option value="Asia/Kuala_Lumpur">Malaysia (Kuala Lumpur) - UTC+8</option>
                  <option value="Asia/Jakarta">Indonesia (Jakarta) - UTC+7</option>
                  <option value="Asia/Seoul">South Korea (Seoul) - UTC+9</option>
                  <option value="Asia/Taipei">Taiwan (Taipei) - UTC+8</option>
                </select>
              </div>
            </div>
          </div>

          {/* Availability */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-luma-text">Availability</h3>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="availableToday"
                checked={formData.availableToday}
                onChange={(e) => setFormData(prev => ({ ...prev, availableToday: e.target.checked }))}
                className="w-4 h-4 text-luma-primary border-gray-300 rounded focus:ring-luma-primary"
              />
              <label htmlFor="availableToday" className="ml-2 text-sm text-gray-700">
                Available today
              </label>
            </div>
          </div>

          {/* Specialties */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-luma-text">Specialties *</h3>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {specialties.map((specialty) => (
                <label
                  key={specialty.id}
                  className="flex items-center p-2 border rounded-lg cursor-pointer hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={formData.selectedSpecialties.includes(specialty.name)}
                    onChange={() => handleSpecialtyToggle(specialty.name)}
                    className="w-4 h-4 text-luma-primary border-gray-300 rounded focus:ring-luma-primary"
                  />
                  <span className="ml-2 text-sm text-gray-700">{specialty.name}</span>
                </label>
              ))}
            </div>
            {allErrors.specialties && <p className="text-red-500 text-sm mt-1">{allErrors.specialties}</p>}
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-luma-primary text-white rounded-lg hover:bg-luma-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {editingProfessional ? 'Update Professional' : 'Save Professional'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default HealthProfessionalForm;